/** 파일 업로드 섹션 컴포넌트 */

import { useAcceptedFileSelect } from "@/hooks/use-accepted-file-select";
import { cn } from "@/lib/utils";
import { FileText, Loader2, Upload } from "lucide-react";
import { type ChangeEvent, type DragEvent, useRef, useState } from "react";

interface FileUploadSectionProps {
  label: string;
  accept: string;
  onFileSelect: (file: File) => void;
  isLoading: boolean;
  fileName?: string;
  description?: string;
  accentColor: "blue" | "purple";
  className?: string;
}

export const FileUploadSection = ({
  label,
  accept,
  onFileSelect,
  isLoading,
  fileName,
  description,
  accentColor,
  className,
}: FileUploadSectionProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = useAcceptedFileSelect({
    accept,
    inputRef: fileInputRef,
    onFileSelect,
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(event.target.files?.[0]);
  };

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    handleFileSelect(event.dataTransfer.files?.[0]);
  };

  const colors = {
    blue: {
      border: isDragOver ? "border-blue-500 bg-blue-50" : "border-neutral-200",
      iconBg: "bg-blue-50",
      iconColor: "text-blue-600",
      badge: "bg-blue-100 text-blue-700",
    },
    purple: {
      border: isDragOver
        ? "border-purple-500 bg-purple-50"
        : "border-neutral-200",
      iconBg: "bg-purple-50",
      iconColor: "text-purple-600",
      badge: "bg-purple-100 text-purple-700",
    },
  };

  const c = colors[accentColor];

  return (
    <div
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        "flex flex-1 min-w-0 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-8 transition-all",
        c.border,
        "hover:bg-neutral-50",
        className,
      )}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleChange}
        accept={accept}
        className="hidden"
      />
      <div
        className={cn(
          "mb-4 flex size-16 items-center justify-center rounded-2xl",
          c.iconBg,
        )}
      >
        {isLoading ? (
          <Loader2 className={cn("size-8 animate-spin", c.iconColor)} />
        ) : fileName ? (
          <FileText className={cn("size-8", c.iconColor)} />
        ) : (
          <Upload className={cn("size-8", c.iconColor)} />
        )}
      </div>
      <span
        className={cn(
          "mb-3 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold",
          c.badge,
        )}
      >
        {label}
      </span>
      {fileName ? (
        <p className="max-w-xs truncate text-center text-sm font-medium text-neutral-900">
          {fileName}
        </p>
      ) : (
        <>
          <p className="text-center text-sm text-neutral-600">
            클릭하거나 파일을 드래그해서 업로드
          </p>
          {description && (
            <p className="mt-1 text-xs text-neutral-400">{description}</p>
          )}
        </>
      )}
    </div>
  );
};
